// core/data/live.ts — typed seed for the placeholder Live page.
//
// Creators are NOT new placeholder accounts: they're the same six existing
// seeded users core/data/videos.ts reuses (core/data/users.ts, mirrored
// server-side in backend/apps/accounts/management/commands/seed.py's AUTHORS
// list), so Follow genuinely works through the real useFollow ->
// followsService -> /api/users/<handle>/follow/ path with zero new backend
// records.
//
// Chat `authorName` always uses the creator's display NAME (e.g. "Jordan Lee"),
// never the bare handle (e.g. "jlee") — a mixed convention read as an
// inconsistency, not two intentionally different fields.
//
// Viewer counts and LIVE badges are frozen placeholder numbers — nothing here
// is really live. LivePage/LiveDetailPage carry an explicit preview notice so
// that reads as an unbuilt feature, not a broken one (see meta.ts's
// PREVIEW_NOTICE).
//
// PORTED to mobile/src/core/data/live.ts. Keep both in sync.

import type { LiveChannel } from '@/core/models/live';
import { seedCreators } from './creators';

/**
 * Top Live Creators rail — denormalized display data for real seeded accounts
 * (core/data/users.ts / backend seed.py AUTHORS), so Follow works through the
 * genuine useFollow -> followsService path with zero new backend records.
 * Re-exports the shared `seedCreators` array (core/data/creators.ts) — Videos'
 * `seedVideoCreators` re-exports the same array (slice-4-xl S4: these used to
 * be two byte-identical copies).
 */
export const seedLiveCreators = seedCreators;

export const seedLiveChannels: LiveChannel[] = [
  {
    id: 'live-01',
    title: 'Open Mic: the five-category moderation model, live Q&A',
    description:
      'Taking questions on why the platform draws the line at threats, terrorism, scams, fraud and coordinated abuse. Nothing more, nothing less.',
    authorId: 'freedom-seeker',
    category: 'news-politics',
    tags: ['#FreeSpeech', '#Moderation'],
    viewerCount: 8_200,
    featured: true,
    chat: [
      { id: 'c1', authorName: 'LibertyMind', body: 'Been waiting for this one all week.' },
      { id: 'c2', authorName: 'Jordan Lee', body: 'Can you cover appeals next?' },
      { id: 'c3', authorName: 'Sara Kim', body: 'The five-category thing finally makes sense to me now.' },
      { id: 'c4', authorName: 'Maya Patel', body: 'Rotating council teams > fixed mods, change my mind.' },
    ],
  },
  {
    id: 'live-02',
    title: 'Late-night modding session: server-first, always',
    description: 'Keeping a decade-old game alive with community servers and mods the studio never shipped.',
    authorId: 'sara-kim',
    category: 'gaming',
    tags: ['#Gaming', '#Modding'],
    viewerCount: 3_400,
    chat: [
      { id: 'c1', authorName: 'Jordan Lee', body: 'That texture pack is unreal.' },
      { id: 'c2', authorName: 'OpenMind', body: 'How long has this server been running?' },
      { id: 'c3', authorName: 'Maya Patel', body: 'o7 to the maintainers.' },
    ],
  },
  {
    id: 'live-03',
    title: 'Building a decentralized identity layer, from scratch',
    description: 'A live build stream: portable identity, no platform lock-in, explained as we go.',
    authorId: 'liberty-mind',
    category: 'technology',
    tags: ['#Decentralization', '#Identity'],
    viewerCount: 5_900,
    chat: [
      { id: 'c1', authorName: 'FreedomSeeker', body: 'This is the thread I needed.' },
      { id: 'c2', authorName: 'Sara Kim', body: 'Does this work offline too?' },
      { id: 'c3', authorName: 'Jordan Lee', body: 'Portable identity is the whole game.' },
    ],
  },
  {
    id: 'live-04',
    title: 'Office hours: keeping open-source maintainers funded',
    description: 'Live office hours on sustainable funding models for the unglamorous work holding up the open web.',
    authorId: 'jordan-lee',
    category: 'education',
    tags: ['#OpenSource', '#Funding'],
    viewerCount: 2_100,
    chat: [
      { id: 'c1', authorName: 'LibertyMind', body: 'Sponsoring three maintainers after this stream.' },
      { id: 'c2', authorName: 'OpenMind', body: 'What about corporate sponsorship strings attached?' },
      { id: 'c3', authorName: 'FreedomSeeker', body: 'This should be required watching tbh.' },
    ],
  },
  {
    id: 'live-05',
    title: 'Watch party: the best privacy toolkit demos of the year',
    description: 'Live reactions and a running tally of the tools that actually moved the needle in 2026.',
    authorId: 'maya-patel',
    category: 'entertainment',
    tags: ['#Privacy', '#WatchParty'],
    viewerCount: 6_700,
    chat: [
      { id: 'c1', authorName: 'Sara Kim', body: 'That one tool alone was worth the stream.' },
      { id: 'c2', authorName: 'Jordan Lee', body: 'Add a link dump after, please!' },
      { id: 'c3', authorName: 'OpenMind', body: 'This is such a good format.' },
    ],
  },
  {
    id: 'live-06',
    title: 'Synthwave set: streaming the late-night build playlist',
    description: 'An hour of live-mixed synthwave for whatever you are building tonight.',
    authorId: 'open-mind',
    category: 'music',
    tags: ['#Synthwave', '#LoFi'],
    viewerCount: 4_300,
    chat: [
      { id: 'c1', authorName: 'Maya Patel', body: 'This track is going straight into my playlist.' },
      { id: 'c2', authorName: 'FreedomSeeker', body: 'Perfect coding-at-2am energy.' },
      { id: 'c3', authorName: 'Jordan Lee', body: 'Take requests?' },
    ],
  },
  {
    id: 'live-07',
    title: 'Round table: good-faith debate in an age of dunking',
    description: 'A live, unscripted conversation about the discipline of listening harder online.',
    authorId: 'sara-kim',
    category: 'talk',
    tags: ['#Community', '#Debate'],
    viewerCount: 1_800,
    chat: [
      { id: 'c1', authorName: 'LibertyMind', body: 'Needed to hear this today.' },
      { id: 'c2', authorName: 'OpenMind', body: 'Good-faith debate is a skill, not a personality trait.' },
      { id: 'c3', authorName: 'Maya Patel', body: 'Bring this guest back.' },
    ],
  },
  {
    id: 'live-08',
    title: 'Community server spotlight: three years, zero downtime',
    description: 'Touring a community-run server that has outlived the studio that made the game.',
    authorId: 'jordan-lee',
    category: 'gaming',
    tags: ['#Gaming', '#Community'],
    viewerCount: 2_950,
    chat: [
      { id: 'c1', authorName: 'Sara Kim', body: 'Three years is wild for a volunteer-run server.' },
      { id: 'c2', authorName: 'FreedomSeeker', body: 'The uptime graph is genuinely impressive.' },
      { id: 'c3', authorName: 'OpenMind', body: 'Shout out to the volunteer admins.' },
    ],
  },
];
